import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Switch, FormControlLabel,
  Select, MenuItem, IconButton, Divider, CircularProgress, Alert, Chip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { adminFetch } from '../../lib/adminAuth';
import { isWithinUploadLimit, tooLargeMessage } from '../../lib/uploadLimits';

const EMPTY_SECTION = { type: 'text', heading: '', content: '', items: [], url: '', icon: '', text: '' };

/* ─── Image field: upload a file, or paste a URL ───
   Uploading is the primary path; the URL box stays so existing links keep
   working and an externally hosted image can still be pasted in. */
const ImageField = ({ label, value, onChange, previewHeight = 200 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('That file is not an image.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (!isWithinUploadLimit(file)) {
      setError(tooLargeMessage());
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await adminFetch('/api/admin/resources/upload-image', { method: 'POST', body: fd });
      if (res.status === 413) {
        throw new Error('That file is too large for the server to accept.');
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Upload failed');
      onChange(data.url);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      // Clear the input so re-picking the same file fires onChange again.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          fullWidth
          label={label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          placeholder="Upload a file, or paste an image URL"
          sx={{ flex: '1 1 240px' }}
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25 }}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
        {value && (
          <Button
            onClick={() => onChange('')}
            disabled={uploading}
            sx={{ textTransform: 'none', color: '#999', mt: 0.25 }}
          >
            Remove
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mt: 1, borderRadius: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {value && (
        <Box
          component="img"
          src={value}
          alt=""
          sx={{ width: '100%', maxHeight: previewHeight, objectFit: 'cover', borderRadius: 1.5, mt: 1.5 }}
        />
      )}
    </Box>
  );
};

/* ─── Section Editor ─── */
const SectionEditor = ({ section, index, total, onChange, onMove, onDelete }) => {
  const update = (field, value) => {
    onChange(index, { ...section, [field]: value });
  };

  const updateHighlightItem = (i, field, value) => {
    const items = [...(section.items || [])];
    items[i] = { ...items[i], [field]: value };
    onChange(index, { ...section, items });
  };

  const addHighlightItem = () => {
    onChange(index, { ...section, items: [...(section.items || []), { icon: 'fa-solid fa-star', text: '' }] });
  };

  const removeHighlightItem = (i) => {
    const items = [...(section.items || [])];
    items.splice(i, 1);
    onChange(index, { ...section, items });
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e8e8e8', borderRadius: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip label={`#${index + 1}`} size="small" sx={{ fontSize: 11, bgcolor: '#f5f5f5' }} />
        <Select
          value={section.type}
          onChange={(e) => update('type', e.target.value)}
          size="small"
          sx={{ minWidth: 140, fontSize: 13 }}
        >
          <MenuItem value="text">Text</MenuItem>
          <MenuItem value="highlights">Highlights</MenuItem>
          <MenuItem value="image">Image</MenuItem>
          <MenuItem value="callout">Callout</MenuItem>
        </Select>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.3 }}>
          <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, -1)}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(index, 1)}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" onClick={() => onDelete(index)}><DeleteIcon sx={{ fontSize: 16, color: '#c62828' }} /></IconButton>
        </Box>
      </Box>

      {section.type === 'text' && (
        <>
          <TextField fullWidth label="Heading" value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} size="small" sx={{ mb: 1.5 }} />
          <TextField fullWidth label="Content" value={section.content || ''} onChange={(e) => update('content', e.target.value)} size="small" multiline rows={3} />
        </>
      )}

      {section.type === 'highlights' && (
        <>
          <TextField fullWidth label="Heading" value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} size="small" sx={{ mb: 1.5 }} />
          {(section.items || []).map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField label="Icon" value={item.icon || ''} onChange={(e) => updateHighlightItem(i, 'icon', e.target.value)} size="small" sx={{ width: 200 }} />
              <TextField label="Text" value={item.text || ''} onChange={(e) => updateHighlightItem(i, 'text', e.target.value)} size="small" sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => removeHighlightItem(i)}><DeleteIcon sx={{ fontSize: 16, color: '#999' }} /></IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addHighlightItem} sx={{ textTransform: 'none', fontSize: 12, color: '#1a1a2e' }}>Add Item</Button>
        </>
      )}

      {section.type === 'image' && (
        <ImageField label="Image" value={section.url} onChange={(v) => update('url', v)} />
      )}

      {section.type === 'callout' && (
        <>
          <TextField fullWidth label="Icon (e.g. fa-solid fa-lightbulb)" value={section.icon || ''} onChange={(e) => update('icon', e.target.value)} size="small" sx={{ mb: 1.5 }} />
          <TextField fullWidth label="Text" value={section.text || ''} onChange={(e) => update('text', e.target.value)} size="small" multiline rows={2} />
        </>
      )}
    </Paper>
  );
};

/* ─── Preview ─── */
const PreviewPane = ({ title, subtitle, banner, sections }) => (
  <Box sx={{ maxWidth: 380, mx: 'auto', bgcolor: '#fff', borderRadius: 3, overflow: 'hidden', border: '1px solid #e8e8e8' }}>
    {banner && <Box component="img" src={banner} alt="" sx={{ width: '100%', height: 180, objectFit: 'cover' }} />}
    <Box sx={{ p: 2.5 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>{title || 'Untitled'}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12, color: '#999', mb: 2 }}>{subtitle}</Typography>}
      <Divider sx={{ mb: 2 }} />
      {(sections || []).map((s, i) => (
        <Box key={i} sx={{ mb: 2 }}>
          {s.type === 'text' && (
            <>
              {s.heading && <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', mb: 0.5 }}>{s.heading}</Typography>}
              <Typography sx={{ fontSize: 12, color: '#555', lineHeight: 1.7 }}>{s.content}</Typography>
            </>
          )}
          {s.type === 'highlights' && (
            <>
              {s.heading && <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', mb: 1 }}>{s.heading}</Typography>}
              {(s.items || []).map((item, j) => (
                <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.8, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#e5a100', mt: 0.8, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: '#555' }}>{item.text}</Typography>
                </Box>
              ))}
            </>
          )}
          {s.type === 'image' && s.url && (
            <Box component="img" src={s.url} alt="" sx={{ width: '100%', borderRadius: 1.5, maxHeight: 160, objectFit: 'cover' }} />
          )}
          {s.type === 'callout' && (
            <Box sx={{ bgcolor: '#fef9eb', borderRadius: 2, p: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12, color: '#e5a100' }}>!</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>{s.text}</Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  </Box>
);

/* ─── Main Editor ─── */
export default function AdminResourceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [card, setCard] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [banner, setBanner] = useState('');
  const [sections, setSections] = useState([]);
  const [published, setPublished] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch(`/api/admin/resources/${id}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        const r = data.resource;
        setCard(r.slug || '');
        setTitle(r.title || '');
        setSubtitle(r.subtitle || '');
        setBanner(r.banner || '');
        setSections(r.sections || []);
        setPublished(r.published || false);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleSave = async (pub) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const body = { title, subtitle, banner, sections, published: pub !== undefined ? pub : published };
      const res = await adminFetch(`/api/admin/resources/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (i, updated) => {
    const arr = [...sections];
    arr[i] = updated;
    setSections(arr);
  };

  const moveSection = (i, dir) => {
    const arr = [...sections];
    const j = i + dir;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSections(arr);
  };

  const deleteSection = (i) => {
    setSections(sections.filter((_, idx) => idx !== i));
  };

  const addSection = (type) => {
    setSections([...sections, { ...EMPTY_SECTION, type }]);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress size={28} sx={{ color: '#1a1a2e' }} /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/resources')} size="small"><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Edit Resource</Typography>
          {card && (
            <Typography sx={{ fontSize: 12, color: '#999', mt: 0.3 }}>
              Opened by the &ldquo;{card.replace(/-/g, ' ')}&rdquo; card on the mobile home page
            </Typography>
          )}
        </Box>
        <Button
          startIcon={<VisibilityIcon />}
          onClick={() => setShowPreview(!showPreview)}
          sx={{ textTransform: 'none', color: showPreview ? '#1a1a2e' : '#999', fontSize: 13 }}
        >
          {showPreview ? 'Hide Preview' : 'Preview'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Editor */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #e8e8e8', borderRadius: 2, mb: 2 }}>
            <TextField
              fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)}
              size="small" sx={{ mb: 2 }}
              helperText="Shown as the heading at the top of the article in the app"
            />
            <TextField fullWidth label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} size="small" sx={{ mb: 2 }} />
            <ImageField label="Banner Image" value={banner} onChange={setBanner} previewHeight={140} />
          </Paper>

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', mb: 1 }}>Sections ({sections.length})</Typography>

          {sections.map((section, i) => (
            <SectionEditor
              key={i}
              section={section}
              index={i}
              total={sections.length}
              onChange={updateSection}
              onMove={moveSection}
              onDelete={deleteSection}
            />
          ))}

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {['text', 'highlights', 'image', 'callout'].map((type) => (
              <Button
                key={type}
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addSection(type)}
                sx={{ textTransform: 'none', fontSize: 12, color: '#555', border: '1px solid #e0e0e0', borderRadius: 1.5 }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={published} onChange={(e) => setPublished(e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>Published</Typography>}
            />
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button
                startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                onClick={() => handleSave()}
                disabled={saving}
                sx={{
                  bgcolor: '#1a1a2e', color: '#fff', textTransform: 'none', borderRadius: 2, px: 3,
                  '&:hover': { bgcolor: '#2a2a4e' },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Preview */}
        {showPreview && (
          <Box sx={{ width: 420, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, color: '#999', mb: 1.5, textAlign: 'center' }}>Mobile Preview</Typography>
            <PreviewPane title={title} subtitle={subtitle} banner={banner} sections={sections} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
